import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { firstValueFrom, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, timeout, catchError } from 'rxjs/operators';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { SubuserPermissionsService, ClientPageItem } from '@services/subuser-permissions.service';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { isArabicLang, ensureArabicFont, arabicAwareCellHook } from 'src/app/utils/arabic-pdf.util';

export interface PolicyPageItem extends ClientPageItem {
  can_view: boolean;
}

export interface PolicyPreset {
  id: string;
  nameKey: string;
  descKey: string;
  icon: string;
  color: string;
  allowedSlugs: string[];
}

export interface ModuleGroup {
  name: string;
  icon: string;
  items: PolicyPageItem[];
  allowedCount: number;
}

@Component({
  selector: 'app-policy-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NzSwitchModule,
    NzModalModule,
    NzToolTipModule,
    NzDropDownModule,
  ],
  templateUrl: './policy-management.component.html',
  styleUrls: ['./policy-management.component.scss'],
})
export class PolicyManagementComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private permissionsService = inject(SubuserPermissionsService);
  private pagesService = inject(PagesService);
  private sharedService = inject(SharedService);
  private secureStorage = inject(SecureStorageService);
  private modal = inject(NzModalService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // State
  users: any[] = [];
  selectedUserId: number | null = null;
  selectedUser: any = null;

  clientPages: ClientPageItem[] = [];
  userPermissions: PolicyPageItem[] = [];
  originalPermissions: PolicyPageItem[] = [];
  filteredPermissions: PolicyPageItem[] = [];

  isLoadingUsers = false;
  isLoadingPermissions = false;
  isSaving = false;
  isSubUser = false;

  searchTerm = '';
  selectedGroup = 'all';
  statusFilter: 'all' | 'allowed' | 'restricted' = 'all';
  viewMode: 'grid' | 'table' = 'grid';

  // Presets definition
  presets: PolicyPreset[] = [
    {
      id: 'admin',
      nameKey: 'policy-management.preset-admin',
      descKey: 'policy-management.preset-admin-desc',
      icon: 'fa-solid fa-crown',
      color: '#7928CA',
      allowedSlugs: ['*'],
    },
    {
      id: 'campaign_manager',
      nameKey: 'policy-management.preset-campaign',
      descKey: 'policy-management.preset-campaign-desc',
      icon: 'fa-solid fa-bullhorn',
      color: '#0284C7',
      allowedSlugs: [
        'dashboard',
        'quick-actions',
        'contests',
        'quiz',
        'participants',
        'web-qr',
        'analytic',
        'monitoring',
        'whatsapp-logs',
        'whatsapp',
      ],
    },
    {
      id: 'developer',
      nameKey: 'policy-management.preset-developer',
      descKey: 'policy-management.preset-developer-desc',
      icon: 'fa-solid fa-code',
      color: '#10B981',
      allowedSlugs: [
        'dashboard',
        'developer',
        'sdk-documentation',
        'sdk-applications',
        'branding',
        '3d-model',
        'luqta-webhook',
        'activity-logs',
      ],
    },
    {
      id: 'viewer',
      nameKey: 'policy-management.preset-viewer',
      descKey: 'policy-management.preset-viewer-desc',
      icon: 'fa-solid fa-eye',
      color: '#F59E0B',
      allowedSlugs: ['dashboard', 'activity-logs', 'monitoring', 'whatsapp-logs'],
    },
    {
      id: 'restricted',
      nameKey: 'policy-management.preset-restricted',
      descKey: 'policy-management.preset-restricted-desc',
      icon: 'fa-solid fa-lock',
      color: '#EF4444',
      allowedSlugs: [],
    },
  ];

  ngOnInit(): void {
    // 1. Instantly hide any active global loaders
    this.sharedService.hideLoader();

    const userDetails = this.secureStorage.getItem<any>('userDetails');
    this.isSubUser = !!userDetails?.isSubUser || !!userDetails?.subuser_id || !!userDetails?.is_sub_user;

    // 2. Instant fast render: populate all pages immediately so user never sees a blank screen or stuck loader
    this.clientPages = this.permissionsService.getFallbackClientPages();
    this.userPermissions = this.clientPages.map((p) => ({ ...p, can_view: true }));
    this.originalPermissions = JSON.parse(JSON.stringify(this.userPermissions));
    this.applyFilter();

    // 3. Search debouncing
    this.searchSubject
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyFilter();
      });

    // 4. Check query params for pre-selected user
    const initialQueryId = this.route.snapshot.queryParams['userId'];
    if (initialQueryId) {
      this.selectedUserId = Number(initialQueryId);
    }

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const qUserId = params['userId'] ? Number(params['userId']) : null;
      if (qUserId && qUserId !== this.selectedUserId) {
        this.selectedUserId = qUserId;
        if (this.users.length > 0) {
          this.selectedUser = this.users.find((u) => Number(u.id ?? u.sub_user_id) === qUserId) || null;
        }
        this.loadPermissionsForSelectedUser();
      }
    });

    // 5. Load fresh data in background
    this.initializeData();
  }

  ngOnDestroy(): void {
    this.sharedService.hideLoader();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load subusers and fresh client pages with guaranteed timeout
  async initializeData(): Promise<void> {
    this.sharedService.hideLoader();
    this.isLoadingUsers = true;
    try {
      // 1. Fetch subusers with 4s timeout
      const res: any = await firstValueFrom(
        this.pagesService.getAll('GET_SUBUSERS', { page: 1, per_page: 100 }).pipe(
          timeout(4000),
          catchError(() => of({ items: [] }))
        )
      );
      this.users = res?.items || res?.data || [];

      // Determine active user
      if (this.users.length > 0) {
        if (this.selectedUserId) {
          this.selectedUser = this.users.find((u) => Number(u.id ?? u.sub_user_id) === this.selectedUserId) || this.users[0];
        } else {
          this.selectedUser = this.users[0];
        }
        this.selectedUserId = Number(this.selectedUser.id ?? this.selectedUser.sub_user_id);
        await this.loadPermissionsForSelectedUser();
      } else {
        this.selectedUser = null;
        this.selectedUserId = null;
        this.isLoadingPermissions = false;
        this.applyFilter();
      }
    } catch (err) {
      console.warn('[PolicyManagement] initializeData fallback used:', err);
      this.isLoadingPermissions = false;
      this.applyFilter();
    } finally {
      this.isLoadingUsers = false;
      this.isLoadingPermissions = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  async selectUser(user: any): Promise<void> {
    if (!user) return;
    if (this.hasUnsavedChanges) {
      this.modal.confirm({
        nzTitle: 'Unsaved Changes',
        nzContent: 'You have unsaved policy changes for this user. Do you want to discard them and switch?',
        nzOkText: 'Discard & Switch',
        nzCancelText: 'Keep Editing',
        nzOkDanger: true,
        nzOnOk: () => {
          this.switchUserConfirmed(user);
        },
      });
    } else {
      this.switchUserConfirmed(user);
    }
  }

  private switchUserConfirmed(user: any): void {
    this.selectedUser = user;
    this.selectedUserId = Number(user.id ?? user.sub_user_id);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { userId: this.selectedUserId },
      queryParamsHandling: 'merge',
    });
    this.loadPermissionsForSelectedUser();
  }

  async loadPermissionsForSelectedUser(): Promise<void> {
    if (!this.clientPages || this.clientPages.length === 0) {
      this.clientPages = this.permissionsService.getFallbackClientPages();
    }

    if (!this.selectedUserId) {
      this.isLoadingPermissions = false;
      this.applyFilter();
      return;
    }

    this.isLoadingPermissions = true;
    try {
      // Fetch user permissions with 3.5s timeout so UI never hangs
      const userPerms = await firstValueFrom(
        this.permissionsService.getSubuserPermissions(this.selectedUserId).pipe(
          timeout(3500),
          catchError(() => of([]))
        )
      );

      if (Array.isArray(userPerms) && userPerms.length > 0) {
        this.userPermissions = this.clientPages.map((page) => {
          const matched = userPerms.find(
            (up: any) =>
              (up.page_id && up.page_id === (page.page_id ?? page.id)) ||
              (up.slug && up.slug === page.slug) ||
              (up.page_name && up.page_name?.toLowerCase() === page.name?.toLowerCase())
          );
          return {
            ...page,
            can_view: matched ? matched.can_view !== false : true,
          };
        });
      } else {
        this.userPermissions = this.clientPages.map((page) => ({
          ...page,
          can_view: true,
        }));
      }

      this.originalPermissions = JSON.parse(JSON.stringify(this.userPermissions));
      this.applyFilter();
    } catch (err) {
      console.warn('[PolicyManagement] loadPermissionsForSelectedUser fallback used:', err);
      this.userPermissions = this.clientPages.map((p) => ({ ...p, can_view: true }));
      this.originalPermissions = JSON.parse(JSON.stringify(this.userPermissions));
      this.applyFilter();
    } finally {
      this.isLoadingPermissions = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  // Filter & Search
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  applyFilter(): void {
    const term = (this.searchTerm || '').toLowerCase().trim();
    const group = this.selectedGroup;
    const status = this.statusFilter;

    this.filteredPermissions = this.userPermissions.filter((item) => {
      // Group filter
      if (group !== 'all' && item.group !== group) return false;

      // Status filter
      if (status === 'allowed' && !item.can_view) return false;
      if (status === 'restricted' && item.can_view) return false;

      // Text search
      if (term) {
        const nameMatch = (item.name || '').toLowerCase().includes(term);
        const slugMatch = (item.slug || '').toLowerCase().includes(term);
        const routeMatch = (item.route || '').toLowerCase().includes(term);
        const groupMatch = (item.group || '').toLowerCase().includes(term);
        const descMatch = (item.description || '').toLowerCase().includes(term);
        if (!nameMatch && !slugMatch && !routeMatch && !groupMatch && !descMatch) {
          return false;
        }
      }

      return true;
    });
    this.cdr.detectChanges();
  }

  // Toggling & Permissions changes
  togglePermission(item: PolicyPageItem): void {
    item.can_view = !item.can_view;
    this.applyFilter();
  }

  grantAll(): void {
    this.userPermissions.forEach((p) => (p.can_view = true));
    this.applyFilter();
  }

  revokeAll(): void {
    this.userPermissions.forEach((p) => (p.can_view = false));
    this.applyFilter();
  }

  resetChanges(): void {
    this.userPermissions = JSON.parse(JSON.stringify(this.originalPermissions));
    this.applyFilter();
    this.sharedService.createNotification('info', 'Policy restored to last saved state.');
  }

  applyPreset(preset: PolicyPreset): void {
    if (preset.allowedSlugs.includes('*')) {
      this.userPermissions.forEach((p) => (p.can_view = true));
    } else if (preset.allowedSlugs.length === 0) {
      this.userPermissions.forEach((p) => (p.can_view = false));
    } else {
      this.userPermissions.forEach((p) => {
        const slug = (p.slug || '').toLowerCase().trim();
        const route = (p.route || '').replace(/^\/home\/?/, '').replace(/^\//, '').toLowerCase().trim();
        p.can_view = preset.allowedSlugs.some(
          (s) => s.toLowerCase() === slug || s.toLowerCase() === route || route.includes(s.toLowerCase())
        );
      });
    }
    this.applyFilter();
    const presetName = this.translate.instant(preset.nameKey) || preset.id;
    this.sharedService.createNotification('info', `Applied preset: "${presetName}". Click "Save Policy" to persist.`);
  }

  // Save Policy to backend
  async savePolicy(): Promise<void> {
    if (!this.selectedUserId || this.isSaving) return;
    this.isSaving = true;
    try {
      await firstValueFrom(
        this.permissionsService.updateSubuserPermissionsBulk(this.selectedUserId, this.userPermissions).pipe(
          timeout(5000)
        )
      );
      this.originalPermissions = JSON.parse(JSON.stringify(this.userPermissions));
      this.sharedService.createNotification('success', 'Policy updated successfully');
    } catch (err: any) {
      console.error('Failed to save policy:', err);
      const msg = err?.error?.message || err?.message || 'Failed to update policy';
      this.sharedService.createNotification('error', msg);
    } finally {
      this.isSaving = false;
      this.sharedService.hideLoader();
      this.cdr.detectChanges();
    }
  }

  // Getters & Stats
  get hasUnsavedChanges(): boolean {
    if (!this.originalPermissions || this.originalPermissions.length === 0) return false;
    return this.userPermissions.some((p) => {
      const orig = this.originalPermissions.find((o) => (o.page_id ?? o.id) === (p.page_id ?? p.id));
      return orig && orig.can_view !== p.can_view;
    });
  }

  get unsavedChangesCount(): number {
    if (!this.originalPermissions || this.originalPermissions.length === 0) return 0;
    return this.userPermissions.filter((p) => {
      const orig = this.originalPermissions.find((o) => (o.page_id ?? o.id) === (p.page_id ?? p.id));
      return orig && orig.can_view !== p.can_view;
    }).length;
  }

  get totalCount(): number {
    return this.userPermissions.length;
  }

  get allowedCount(): number {
    return this.userPermissions.filter((p) => p.can_view).length;
  }

  get restrictedCount(): number {
    return this.totalCount - this.allowedCount;
  }

  get coveragePercentage(): number {
    if (this.totalCount === 0) return 100;
    return Math.round((this.allowedCount / this.totalCount) * 100);
  }

  get coverageStatus(): { label: string; class: string } {
    const pct = this.coveragePercentage;
    if (pct === 100) return { label: 'Full Access', class: 'status-full' };
    if (pct >= 60) return { label: 'Custom Access', class: 'status-moderate' };
    if (pct > 0) return { label: 'Restricted', class: 'status-restricted' };
    return { label: 'Locked Out', class: 'status-locked' };
  }

  get groups(): { name: string; count: number; icon: string }[] {
    const groupIcons: Record<string, string> = {
      'Main': 'fa-solid fa-compass',
      'Engagement': 'fa-solid fa-trophy',
      'Technical & SDK': 'fa-solid fa-code',
      'Insights & Tools': 'fa-solid fa-chart-line',
      'Account & Management': 'fa-solid fa-user-shield',
    };

    const counts: { [key: string]: number } = {};
    this.userPermissions.forEach((p) => {
      const g = p.group || 'General';
      counts[g] = (counts[g] || 0) + 1;
    });
    return Object.keys(counts).map((g) => ({
      name: g,
      count: counts[g],
      icon: groupIcons[g] || 'fa-solid fa-folder',
    }));
  }

  get groupedSections(): ModuleGroup[] {
    const groupMap: { [key: string]: PolicyPageItem[] } = {};
    this.filteredPermissions.forEach((item) => {
      const g = item.group || 'General';
      if (!groupMap[g]) groupMap[g] = [];
      groupMap[g].push(item);
    });

    const groupIcons: Record<string, string> = {
      'Main': 'fa-solid fa-compass',
      'Engagement': 'fa-solid fa-trophy',
      'Technical & SDK': 'fa-solid fa-code',
      'Insights & Tools': 'fa-solid fa-chart-line',
      'Account & Management': 'fa-solid fa-user-shield',
    };

    return Object.keys(groupMap).map((name) => ({
      name,
      icon: groupIcons[name] || 'fa-solid fa-folder',
      items: groupMap[name],
      allowedCount: groupMap[name].filter((p) => p.can_view).length,
    }));
  }

  goBack(): void {
    this.router.navigate(['/home/user-managment']);
  }

  // Export functions
  async exportPDF(): Promise<void> {
    const doc = new jsPDF();
    const isAr = isArabicLang();
    const arabicFontLoaded = await ensureArabicFont(doc);

    const userName = this.selectedUser?.name || this.selectedUser?.email || 'Sub-User';
    const dateStr = new Date().toLocaleDateString();

    doc.setFontSize(18);
    doc.setTextColor(83, 4, 251);
    doc.text('LuQta Policy Management Report', 14, 20);

    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text(`User: ${userName} (${this.selectedUser?.email || '-'})`, 14, 28);
    doc.text(`Role: ${this.selectedUser?.role || 'User'} | Generated: ${dateStr}`, 14, 34);
    doc.text(
      `Access Summary: ${this.allowedCount} Allowed / ${this.restrictedCount} Restricted (${this.coveragePercentage}% coverage)`,
      14,
      40
    );

    const tableData = this.userPermissions.map((p, index) => [
      index + 1,
      p.name || '-',
      p.group || '-',
      p.route || '-',
      p.can_view ? 'Allowed' : 'Restricted',
    ]);

    autoTable(doc, {
      startY: 46,
      head: [['#', 'Page / Policy Name', 'Module Group', 'Route', 'Access Status']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [83, 4, 251],
        textColor: 255,
        font: arabicFontLoaded ? (isAr ? 'Amiri' : 'helvetica') : 'helvetica',
      },
      didParseCell: arabicAwareCellHook(arabicFontLoaded),
    });

    doc.save(`policy_report_${userName.replace(/\s+/g, '_')}.pdf`);
  }

  exportExcel(): void {
    const userName = this.selectedUser?.name || this.selectedUser?.email || 'Sub-User';
    const data = this.userPermissions.map((p, index) => ({
      Index: index + 1,
      'Page / Policy Name': p.name,
      'Module Group': p.group,
      'Route Path': p.route,
      'Access Status': p.can_view ? 'Allowed' : 'Restricted',
      'Assigned User': userName,
      'User Email': this.selectedUser?.email || '-',
      'Export Date': new Date().toISOString(),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Policy Permissions');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `policy_report_${userName.replace(/\s+/g, '_')}.xlsx`);
  }
}
